<template>
  <div class='organizations-settings'>
    <div class='organizations-settings__header'>
      <div class="organizations-settings__header__title">
        {{$t('organization.settings.title')}}
      </div>
      <div class='organizations-settings__header__add'>
        <v-btn class="add-organization-btn" @click="openAddOrganizationPopup">
          {{ $t('add.organization.btn.label') }}
        </v-btn>
      </div>
    </div>
    <div class='organizations-settings__content'>
      <assets-column-headers
        :display='true'
        :headers='headersForTab'
        tab='organizations'
      />
      <organizations-settings-row
        v-for='(organization, index) in organizationsSorted' :key='index'
        :organization='organization'
        :linked-domains='[...getCloudAccountsForOrganization(organization), ...getDomainsForOrganization(organization)]'
        @editOrganization='editOrganization'
        @deleteOrganization='deleteOrganization'
        @openAddTagPopup='openAddTagPopup'
        @removeTag='removeTag'
        @linkDomainsToOrganization='linkDomainsToOrganization'
        @unlinkDomain='unlinkDomain'
        @setDefaultDomain="setDefaultDomain"
      />
    </div>
    <add-organization-popup
      :organization='organizationForPopup'
      :value='addOrganizationPopup'
      :edit-mode='editMode'
      @closed='closeAddOrganizationPopup'
      @updateErrorMessage='(val) => errorMessage = val'
      @changeErrorBanner="changeErrorBanner"
    />
    <yes-no-popup
      :visible-popup='showMessagePopup'
      :message='messageForPopup'
      :show-no-button='showNoButton'
      :show-yes-button='showYesButton'
      no-caption='confirm.cancel'
      :yes-caption='okCaptionForPopup'
      :loading='loadingForPopup'
      :title='messagePopupTitle'
      @clicked='messagePopupClicked'
    />
    <link-domain-to-organization-popup
      :value='showLinkDomainsToOrgPopup'
      :organization='organizationForPopup'
      :loading='loadingForPopup'
      @saveLinkedDomains='saveLinkedDomains'
      @closed='closeLinkDomainsToOrgPopup'
    />
    <change-organization-default-domain
      :value='showSetDefaultDomainPopup'
      :organization='organizationForPopup'
      :loading='loadingForPopup'
      @saveLinkedDomains='saveDefaultDomain'
      @closed='closeSetDefaultDomainPopup'
    />
    <add-tags-popup
      :organization='organizationForPopup'
      :assets='[]'
      :is-for-organization='true'
      :value='showConfigureTagsPopup'
      :loading='loadingForPopup'
      @submitTagsInformation='addNewTags'
      @toggleTagsPopup='showConfigureTagsPopup = false'
    />
    <div v-if="errorMessage">
      <v-alert
        text
        dense
        color="#e0255e"
        icon="info"
        border="left"
        class='not-valid-message'
      >
        {{ errorMessage }}
      </v-alert>
    </div>
    <error-message :errorText="bannerErrorMessage" @close="bannerErrorMessage = ''" />
  </div>
</template>

<script>
import OrganizationsSettingsRow from '@/components/Settings/OrganizationsSettingsRow.vue';
import { mapGetters } from 'vuex';
import AssetsColumnHeaders from '@/components/configuration/AssetsColumnHeaders.vue';
import AddOrganizationPopup from '@/components/popup/AddOrganizationPopup.vue';
import {
  DELETE_ORGANIZATION,
  LOAD_ALL_ORGANIZATIONS, SET_DEFAULT_DOMAIN,
} from '@/store/organization/actionTypes';
import YesNoPopup from '@/components/popup/YesNoPopup.vue';
import { ADD_URL_CONFIGURATIONS, LOAD_DOMAINS_FROM_CONFIGURATION } from '@/store/project/actionTypes';
import { CONFIGURATION, CONFIGURATION_ACTION_TYPES } from '@/constants/configuration';
import LinkDomainToOrganizationPopup from '@/components/popup/LinkDomainsToOrganizationPopup.vue';
import ChangeOrganizationDefaultDomain from '@/components/popup/ChangeOrganizationDefaultDomain.vue';
import AddTagsPopup from '@/components/popup/AddTagsPopup.vue';
import ErrorMessage from '@/components/common/ErrorMessage.vue';

export default {
  name: 'OrganizationsSettings',
  components: {
    AddTagsPopup,
    LinkDomainToOrganizationPopup,
    ChangeOrganizationDefaultDomain,
    YesNoPopup,
    AddOrganizationPopup,
    AssetsColumnHeaders,
    OrganizationsSettingsRow,
    ErrorMessage,
  },
  data() {
    return {
      errorMessage: '',
      editMode: false,
      payloadForUnlinkDomainPopup: {},
      organizationForPopup: {},
      addOrganizationPopup: false,
      showMessagePopup: false,
      showLinkDomainsToOrgPopup: false,
      showConfigureTagsPopup: false,
      messageForPopup: '',
      okCaptionForPopup: '',
      messagePopupTitle: '',
      showNoButton: false,
      showYesButton: true,
      loadingForPopup: false,
      showSetDefaultDomainPopup: false,
      headersForTab: [
        'organizations.settings.tab.name.header',
        'organizations.settings.tab.domains.header',
        'organizations.settings.tab.accounts.header',
        'organizations.settings.tab.date.header',
      ],
      bannerErrorMessage: '',
    };
  },
  computed: {
    ...mapGetters([
      'organizations',
      'cloudAccountsPerOrganization',
      'domainsPerOrganization',
      'companyData',
      'configurationDomains',
      'organizationsTags',
    ]),
    deleteOrganizationDisabled() {
      return this.getDomainsForOrganization(this.organizationForPopup).length;
    },
    organizationsSorted() {
      return [...this.organizations].sort((a, b) => {
        const aDate = a.createdDate;
        const bDate = b.createdDate;
        if (a.name === this.companyData.name) {
          return -1;
        }
        if (b.name === this.companyData.name) {
          return 1;
        }
        if (aDate === bDate) {
          return 0;
        }
        return aDate > bDate ? 1 : -1;
      });
    },
  },
  methods: {
    setDefaultDomain(organization) {
      this.showSetDefaultDomainPopup = true;
      this.organizationForPopup = { ...organization };
    },
    closeSetDefaultDomainPopup() {
      this.organizationForPopup = {};
      this.showSetDefaultDomainPopup = false;
    },
    async saveDefaultDomain(payload) {
      this.loadingForPopup = true;
      await this.$store.dispatch(SET_DEFAULT_DOMAIN, payload);
      await this.$store.dispatch(LOAD_ALL_ORGANIZATIONS);
      this.loadingForPopup = false;
      this.showSetDefaultDomainPopup = false;
    },
    async removeTag(payload) {
      const {
        organization,
        tag,
      } = payload;
      const currentOrganizationTags = this.organizationsTags?.[organization.name] || {};
      const tagsUpdated = {...currentOrganizationTags};

      if (Object.keys(currentOrganizationTags).includes(tag)) {
        delete tagsUpdated[tag];
      }
      const configData = [{
        data: organization.name,
        metaData: [],
        tld: null,
        subdomain: null,
        tags: tagsUpdated,
      }];
      await this.$store.dispatch(ADD_URL_CONFIGURATIONS, {
        payload: configData,
        configType: CONFIGURATION.CONFIG_TYPES.TAGS_ORGANIZATION,
        scanEmployees: false,
        successMessageType: payload.action || CONFIGURATION_ACTION_TYPES.ADD_TAGS,
      });
    },
    async addNewTags(payload) {
      const configData = payload.items.map(item => ({
        data: item.url,
        metaData: [],
        tld: null,
        subdomain: null,
        tags: item.tags,
      }));
      await this.$store.dispatch(ADD_URL_CONFIGURATIONS, {
        payload: configData,
        configType: payload.configType,
        scanEmployees: false,
        successMessageType: payload.action || CONFIGURATION_ACTION_TYPES.ADD_TAGS,
      });
    },
    closeLinkDomainsToOrgPopup() {
      this.organizationForPopup = {};
      this.showLinkDomainsToOrgPopup = false;
    },
    async saveLinkedDomains(payload) {
      this.loadingForPopup = true;
      const cloudAccountItems = [];
      const domainItems = [];
      payload.forEach(item => {
        if (item.configType === CONFIGURATION.CONFIG_TYPES.CLOUD_ACCOUNT) {
          cloudAccountItems.push(item);
        } else {
          domainItems.push(item);
        }
      });
      await this.$store.dispatch(ADD_URL_CONFIGURATIONS, {
        payload: cloudAccountItems,
        configType: CONFIGURATION.CONFIG_TYPES.CLOUD_ACCOUNT,
      });
      await this.$store.dispatch(ADD_URL_CONFIGURATIONS, {
        payload: domainItems,
        configType: CONFIGURATION.CONFIG_TYPES.DOMAIN,
      });
      await this.$store.dispatch(LOAD_ALL_ORGANIZATIONS);
      this.closeLinkDomainsToOrgPopup();
      this.loadingForPopup = false;
    },
    async messagePopupClicked(value) {
      if (this.$t(this.messagePopupTitle) === this.$t('delete.organization.title')) {
        if (value && !this.deleteOrganizationDisabled) {
          this.loadingForPopup = true;
          try {
            await this.$store.dispatch(DELETE_ORGANIZATION, this.organizationForPopup.id);
          } catch (e) {
            this.bannerErrorMessage = e.message;
          }
          this.loadingForPopup = false;
        }
        this.organizationForPopup = {};
      } else if (value) {
        this.loadingForPopup = true;
        await this.$store.dispatch(ADD_URL_CONFIGURATIONS, {
          payload: [{
            data: this.payloadForUnlinkDomainPopup.asset, metaData: [], tld: this.payloadForUnlinkDomainPopup.asset, organization: this.companyData,
          }],
          configType: this.payloadForUnlinkDomainPopup.type === 'domain' ? CONFIGURATION.CONFIG_TYPES.DOMAIN : CONFIGURATION.CONFIG_TYPES.CLOUD_ACCOUNT,
        });
        await this.$store.dispatch(LOAD_DOMAINS_FROM_CONFIGURATION);
        await this.$store.dispatch(LOAD_ALL_ORGANIZATIONS);
        this.loadingForPopup = false;
      } else {
        this.payloadForUnlinkDomainPopup = {};
      }
      this.showMessagePopup = false;
    },
    async changeErrorBanner(message) {
      this.bannerErrorMessage = message;
    },
    async closeAddOrganizationPopup() {
      this.addOrganizationPopup = false;
      await this.$store.dispatch(LOAD_ALL_ORGANIZATIONS);
      this.editMode = false;
      this.errorMessage = false;
      this.organizationForPopup = {};
    },
    getCloudAccountsForOrganization(organization) {
      return this.cloudAccountsPerOrganization.get(organization.name) || [];
    },
    getDomainsForOrganization(organization) {
      return this.domainsPerOrganization.get(organization.name) || [];
    },
    linkDomainsToOrganization(organization) {
      this.showLinkDomainsToOrgPopup = true;
      this.organizationForPopup = { ...organization };
    },
    openAddTagPopup(organization) {
      this.showConfigureTagsPopup = true;
      this.organizationForPopup = { ...organization };
    },
    unlinkDomain(payload) {
      this.payloadForUnlinkDomainPopup = {
        asset: payload.asset,
        type: this.configurationDomains.find(item => item.configData === payload.asset) ? 'domain' : 'cloud',
      };
      this.messageForPopup = 'unlink.domain.from.organization.confirm.message';
      this.okCaptionForPopup = 'delete.organization.continue.caption';
      this.messagePopupTitle = 'unlink.domain.from.organization.title';
      this.showNoButton = true;
      this.showMessagePopup = true;
      this.showYesButton = true;
    },
    deleteOrganization(organization) {
      this.organizationForPopup = { ...organization };
      this.messagePopupTitle = 'delete.organization.title';
      if (organization.isSupplyChain) {
        // TPRM supply chain orgs cannot be deleted while an active TPRM license exists
        this.messageForPopup = 'delete.organization.tprm.disabled.message';
        this.okCaptionForPopup = 'delete.organization.ok.caption';
        this.showNoButton = false;
        this.showYesButton = false;
      } else if (this.deleteOrganizationDisabled) {
        this.messageForPopup = 'delete.organization.disabled.message';
        this.okCaptionForPopup = 'delete.organization.ok.caption';
        this.showNoButton = true;
        this.showYesButton = false;
      } else {
        this.messageForPopup = 'delete.organization.confirm.message';
        this.okCaptionForPopup = 'delete.organization.continue.caption';
        this.showNoButton = true;
      }
      this.showMessagePopup = true;
    },
    openAddOrganizationPopup() {
      this.organizationForPopup = {};
      this.addOrganizationPopup = true;
    },
    editOrganization(organization) {
      this.addOrganizationPopup = true;
      this.editMode = true;
      this.organizationForPopup = { ...organization };
    },
  },
};
</script>

<style lang='scss'>
@import '~@/assets/styles/design-system';
.organizations-settings {
  margin-left: 3%;
  padding: 0 rem(86) 0 rem(32);
}
</style>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';
.organizations-settings {
  margin-left: 3%;
  padding: 0 rem(86) 0 rem(32);
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  overflow-x: hidden;
  &__header {
    flex: 0 0 rem(70);
    display: flex;
    justify-content: space-between;
    margin-bottom: 1%;
    margin-top: 2%;
    &__title {
      color: var(--text-primary);
      font-family: $font-family-bold;
      font-size: rem(20);
      font-weight: bold;
      line-height: rem(20);
      letter-spacing: 0;
      margin-bottom: rem(20);
    }
    &__add {
      .add-organization-animation {
        height: rem(30);
        margin-top: rem(-5);
      }
      .add-organization-btn {
        background-color: var(--brand-primary) !important;
        border-radius: $radius-xs !important;
        box-shadow: 0 rem(1) rem(1) 0 rgba(0, 0, 0, 0.14), 0 rem(2) rem(1) rem(-1) rgba(0, 0, 0, 0.12), 0 rem(1) rem(3) 0 rgba(0, 0, 0, 0.2) !important;
        color: #ffffff !important;
        font-family: $font-family-medium;
        font-size: rem(12);
        text-transform: uppercase;
        height: rem(32);
        letter-spacing: rem(1.1);
        line-height: rem(16);
        width: rem(153);
        margin-right: rem(20);
      }
    }
  }
  &__content {
    display: flex;
    flex-direction: column;
  }
}
</style>
